// src/components/profiles/Member/components/posts/moment/MomentGridItem.tsx

"use client";

import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useRouter } from "next/navigation";
import { useAppDispatch } from "../../../../../../redux/hooks";

import {
  deleteMomentBySlug,
  fetchMomentBySlug,
} from "../../../../../../redux/ugc/momentSlice";

import { ChevronLeft, ChevronRight, Edit3, Play, Trash2 } from "lucide-react";

import type {
  MomentDTO,
  MomentImageItem,
} from "../../../../../../types/ugc/momentTypes";

import {
  getMomentImageFieldNames,
  getMomentImageItems,
  getMomentMediaType,
  isMultiPhotoMoment,
} from "../../../../../../types/ugc/momentTypes";

import { makeMediaUrl } from "../../../../../../utils/navigation/contentNavigation";
import { UGC_ENDPOINTS } from "../../../../../../services/ugc/endpoint/endpoints";
import { ActionMenu, Modal } from "../../../../../common";

import MediaConversionPanel from "../../../../../mediaConversion/MediaConversionPanel/MediaConversionPanel";
import { useMediaConversionJobs } from "../../../../../../hooks/mediaConversion/useMediaConversionJobs";

import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";
import type { PrivateCdnTarget } from "@/asset-delivery/components/PrivateCdnImage";
import { toCleanCdnUrl } from "@/asset-delivery/utils/toCleanCdnUrl";

import "./MomentGridItem.css";

type Props = {
  moment: MomentDTO;
};

type GridMediaSource = {
  key: string;
  src: string | null;
  fieldName: string;
  kind: "image" | "thumbnail";
};

const buildPrivateCdnTarget = (m: MomentDTO): PrivateCdnTarget | null => {
  const id = Number((m as any)?.id);

  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }

  return {
    app_label: "posts",
    model: "moment",
    object_id: id,
  };
};

const stopPointer = (e: React.PointerEvent | React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const getImageItemUrl = (item?: MomentImageItem | null): string | null => {
  const anyItem = item as any;

  return (
    toCleanCdnUrl(anyItem?.thumbnail_cdn_url) ||
    toCleanCdnUrl(anyItem?.thumbnail_url) ||
    toCleanCdnUrl(anyItem?.grid_cdn_url) ||
    toCleanCdnUrl(anyItem?.grid_url) ||
    toCleanCdnUrl(anyItem?.preview_cdn_url) ||
    toCleanCdnUrl(anyItem?.preview_url) ||
    toCleanCdnUrl(anyItem?.url) ||
    toCleanCdnUrl(anyItem?.image_url) ||
    toCleanCdnUrl(anyItem?.cdn_url) ||
    toCleanCdnUrl(anyItem?.signed_url) ||
    null
  );
};

const getCoverItem = (moment: MomentDTO): MomentImageItem | undefined => {
  const items = getMomentImageItems(moment);
  if (!items.length) return undefined;

  const coverID = moment.cover_image_id || moment.cover_image?.id || undefined;

  if (coverID) {
    const pinned = items.find((item) => item.id === coverID);
    if (pinned) return pinned;
  }

  const marked = items.find((item) => item.is_cover);
  if (marked) return marked;

  return items[0];
};

const orderItemsForPreview = (moment: MomentDTO): MomentImageItem[] => {
  const items = getMomentImageItems(moment);
  const cover = getCoverItem(moment);

  if (!cover?.id) return items;

  return [
    ...items.filter((item) => item.id === cover.id),
    ...items.filter((item) => item.id !== cover.id),
  ];
};

const buildImageItemSource = (
  moment: MomentDTO,
  item: MomentImageItem,
  fallbackSrc: string | null,
): GridMediaSource => {
  const fieldName = item.field_name || `image_items:${item.id}`;
  const itemSrc = getImageItemUrl(item);

  return {
    key: `${moment.id}:${fieldName}`,
    src: itemSrc || fallbackSrc,
    fieldName,
    kind: "image",
  };
};

const buildGridMediaSources = (
  moment: MomentDTO,
  mediaType: "image" | "video",
): GridMediaSource[] => {
  const cleanThumb = toCleanCdnUrl((moment as any)?.thumbnail);
  const cleanImage = toCleanCdnUrl((moment as any)?.image);

  if (mediaType === "video") {
    return [
      {
        key: `${moment.id}:thumbnail`,
        src: cleanThumb || cleanImage || null,
        fieldName: "thumbnail",
        kind: "thumbnail",
      },
    ];
  }

  const orderedItems = orderItemsForPreview(moment);

  if (orderedItems.length > 0) {
    const coverSrc =
      getImageItemUrl(orderedItems[0]) || cleanImage || cleanThumb || null;

    return orderedItems.map((item, index) =>
      buildImageItemSource(moment, item, index === 0 ? coverSrc : null),
    );
  }

  const fieldNames = getMomentImageFieldNames(moment);

  return fieldNames.map((fieldName, index) => ({
    key: `${moment.id}:${fieldName}`,
    src: index === 0 ? cleanImage || cleanThumb || null : null,
    fieldName,
    kind: fieldName === "thumbnail" ? "thumbnail" : "image",
  }));
};

export default function MomentGridItem({ moment }: Props) {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const didRefetchRef = useRef(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track failed media to avoid retry loops.
  const [failedMediaKeys, setFailedMediaKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const pointerStartRef = useRef<{
    x: number;
    y: number;
    time: number;
  } | null>(null);

  const mediaType = getMomentMediaType(moment);
  const canManage = moment.owner?.is_me === true;
  const multiPhoto = isMultiPhotoMoment(moment);

  const isConverting = moment.is_converted === false;

  const mediaTarget = useMemo(
    () => ({
      by: "slug" as const,
      appLabel: "posts",
      model: "moment",
      slug: moment.slug,
    }),
    [moment.slug],
  );

  const { jobs: conversionJobs } = useMediaConversionJobs(mediaTarget, {
    enabled: isConverting,
    fieldName: "video",
  });

  const hasActiveConversion =
    isConverting ||
    conversionJobs.some(
      (j) => j.status === "queued" || j.status === "processing",
    );

  const href = useMemo(() => {
    if (hasActiveConversion) return "#";

    return makeMediaUrl(moment.slug, {
      endpoint: UGC_ENDPOINTS.moments.root,
      scope: "flat",
      aspect: "auto",
      usePoster: true,
    });
  }, [moment.slug, hasActiveConversion]);

  // Keep target stable unless the real moment id changes.
  const privateTarget = useMemo(
    () => buildPrivateCdnTarget(moment),
    [(moment as any)?.id],
  );

  const mediaSources = useMemo(
    () => buildGridMediaSources(moment, mediaType),
    [moment, mediaType],
  );

  const activeSource = mediaSources[activeIndex] || mediaSources[0];

  const activeMediaKey = useMemo(() => {
    if (!activeSource) return null;
    return `${activeSource.key}:${activeSource.src || "no-src"}`;
  }, [activeSource]);

  const activeMediaFailed = activeMediaKey
    ? failedMediaKeys.has(activeMediaKey)
    : false;

  const canGoPrevious = activeIndex > 0;
  const canGoNext = activeIndex < mediaSources.length - 1;

  const canRenderCdn = Boolean(
    activeSource?.src && privateTarget && !activeMediaFailed,
  );

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteMomentBySlug(moment.slug)).unwrap();
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const actionOptions = useMemo(() => {
    if (!canManage) return [];

    return [
      {
        label: "Edit",
        icon: <Edit3 size={16} />,
        onClick: () => router.push(`/moments/${moment.slug}/edit`),
      },
      {
        label: "Delete",
        icon: <Trash2 size={16} />,
        danger: true,
        onClick: () => setShowDeleteModal(true),
      },
    ];
  }, [canManage, moment.slug, router]);

  useEffect(() => {
    didRefetchRef.current = false;
    setActiveIndex(0);
    setFailedMediaKeys(new Set());
  }, [moment.slug, moment.cover_image_id, mediaSources.length]);

  useEffect(() => {
    if (
      isConverting &&
      conversionJobs.length > 0 &&
      conversionJobs.every(
        (j) => j.status === "done" || j.status === "failed",
      ) &&
      !didRefetchRef.current
    ) {
      didRefetchRef.current = true;
      dispatch(fetchMomentBySlug(moment.slug));
    }
  }, [isConverting, conversionJobs, dispatch, moment.slug]);

  const goPrevious = useCallback(() => {
    setActiveIndex((current) => Math.max(current - 1, 0));
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((current) =>
      Math.min(current + 1, Math.max(mediaSources.length - 1, 0)),
    );
  }, [mediaSources.length]);

  const openMoment = useCallback(() => {
    if (hasActiveConversion) return;
    router.push(href);
  }, [hasActiveConversion, href, router]);

  const handleMediaError = useCallback(() => {
    if (!activeMediaKey) return;

    setFailedMediaKeys((prev) => {
      if (prev.has(activeMediaKey)) return prev;

      const next = new Set(prev);
      next.add(activeMediaKey);
      return next;
    });
  }, [activeMediaKey]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!multiPhoto) return;

    pointerStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now(),
    };
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;

    if (!start || !multiPhoto) {
      openMoment();
      return;
    }

    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const elapsed = Date.now() - start.time;

    const isHorizontalSwipe =
      Math.abs(dx) > 34 && Math.abs(dx) > Math.abs(dy) * 1.25 && elapsed < 900;

    if (isHorizontalSwipe) {
      if (dx < 0) {
        goNext();
      } else {
        goPrevious();
      }

      return;
    }

    openMoment();
  };

  const renderConverting = () => {
    return (
      <div className="moment-grid-item-wrapper">
        <div className="moment-grid-item moment-grid-item__converting">
          <MediaConversionPanel
            target={mediaTarget}
            fieldName="video"
            compact
            title="Processing video"
            density="micro"
          />
        </div>
      </div>
    );
  };

  const renderReady = () => {
    return (
      <>
        <div className="moment-grid-item-wrapper">
          {canManage && (
            <div
              className="moment-grid-item__menu"
              onPointerDown={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <ActionMenu
                options={actionOptions}
                position="bottom-right"
                color="secondary"
                iconSize={28}
                menuWidth="150px"
              />
            </div>
          )}

          <div
            className="moment-grid-item"
            role="button"
            tabIndex={0}
            aria-label="Open moment"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openMoment();
              }

              if (multiPhoto && e.key === "ArrowLeft") {
                e.preventDefault();
                goPrevious();
              }

              if (multiPhoto && e.key === "ArrowRight") {
                e.preventDefault();
                goNext();
              }
            }}
          >
            {canRenderCdn && activeSource && privateTarget ? (
              <PrivateCdnImage
                src={activeSource.src}
                placeholderSrc={null}
                alt=""
                className="moment-grid-item__img is-loaded"
                imgClassName="moment-grid-item__img is-loaded"
                target={privateTarget}
                kind={activeSource.kind}
                fieldName={activeSource.fieldName}
                eager={false}
                rootMargin="350px"
                threshold={0.01}
                failOpen
                imgLoading="lazy"
                fetchPriority="auto"
                onError={handleMediaError}
              />
            ) : (
              <div
                className="moment-grid-item__placeholder"
                aria-hidden="true"
                title={
                  activeMediaFailed
                    ? "Media failed to load"
                    : "No CDN URL available for this item"
                }
              />
            )}

            {mediaType === "video" && (
              <span className="moment-grid-item__videoBadge" aria-hidden="true">
                <Play size={18} />
              </span>
            )}

            {multiPhoto && mediaSources.length > 1 && (
              <>
                <button
                  type="button"
                  className="moment-grid-item__nav moment-grid-item__nav--prev"
                  aria-label="Previous photo"
                  disabled={!canGoPrevious}
                  onPointerDown={stopPointer}
                  onPointerUp={stopPointer}
                  onClick={(e) => {
                    stopPointer(e);
                    goPrevious();
                  }}
                >
                  <ChevronLeft size={18} />
                </button>

                <button
                  type="button"
                  className="moment-grid-item__nav moment-grid-item__nav--next"
                  aria-label="Next photo"
                  disabled={!canGoNext}
                  onPointerDown={stopPointer}
                  onPointerUp={stopPointer}
                  onClick={(e) => {
                    stopPointer(e);
                    goNext();
                  }}
                >
                  <ChevronRight size={18} />
                </button>

                <div
                  className="moment-grid-item__dots"
                  aria-label={`Photo ${activeIndex + 1} of ${mediaSources.length}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {mediaSources.map((source, index) => (
                    <button
                      key={source.key}
                      type="button"
                      className={
                        index === activeIndex
                          ? "moment-grid-item__dot is-active"
                          : "moment-grid-item__dot"
                      }
                      aria-label={`Show photo ${index + 1}`}
                      onPointerDown={stopPointer}
                      onPointerUp={stopPointer}
                      onClick={(e) => {
                        stopPointer(e);
                        setActiveIndex(index);
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Moment"
          message={
            <>
              <p>Are you sure you want to delete this moment?</p>
              <p>
                <strong>This action cannot be undone.</strong>
              </p>
            </>
          }
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
          isLoading={isDeleting}
          onConfirm={handleDelete}
        />
      </>
    );
  };

  if (hasActiveConversion) return renderConverting();

  return renderReady();
}
